export const meta = {
  id: '39-subjunctive-clauses',
  order: 39,
  checkFocus: { mood: ['subjunctive'] },   // what the end-of-lesson check tests first
  titleFi: 'Jos, jotta, kunpa: subjunktiivi sivulauseissa',
  titleEn: 'If, so that, would that: the subjunctive in clauses',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: [
    'lo', 'sepār', 'kessyt', 'drējion', 'pendagon', 'otāpagon', 'vestragon', 'nēdegon',
    'dīnagon', 'keliemagon', 'jemēbagon', 'statagon', 'dārligon', 'ondurilla', 'kīvio',
    'jention', 'nūmāzma', 'iderennon',
    'ours:ademmilla',
    'ours:drējūljagon', 'ours:rhovnon',
    'ours:isīlaksir',
    'ours:pensioni',
    'ours:assuransi',
  ],
  exampleIds: [
    'ex-e61df45a8982', 'got-s3e04-013', 'got-s3e10-003', 'got-s3e09-002', 'hotd-s3e02-003',
    'got-s5e05-002', 'hotd-s3e05-009', 'hotd-s2e04-009', 'hotd-s1e06-010', 'got-s6e08-002',
    'ex-26431af6f541', 'ex-7f3b585ee8d2', 'hotd-s2e07-003', 'hotd-s1e04-006', 'ex-62354dad9567',
    'got-s6e01-002', 'hotd-s1e10-006', 'got-s4e03-003', 'hotd-s1e01-006', 'ex-df10690e339e',
    'ex-13537ed19fcd', 'got-s5e04-011', 'ex-8bf9afdcc233',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Moods',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Conjugation',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=Lo',
    'https://wiki.languageinvention.com/index.php?title=Kessyt',
    'https://wiki.languageinvention.com/index.php?title=Hegnīr',
    'https://wiki.languageinvention.com/index.php?title=Vestragon',
    'https://wiki.languageinvention.com/index.php?title=Kostilus',
    'https://archiveofourown.org/works/24077005/chapters/172067518',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['subjunctive', 'subordinate'],
  showLines: ['got-s3e04-013', 'got-s3e10-003', 'got-s3e09-002', 'hotd-s3e02-003', 'got-s5e05-002', 'hotd-s3e05-009', 'hotd-s2e04-009', 'hotd-s1e06-010', 'got-s6e08-002', 'hotd-s2e07-003', 'hotd-s1e04-006', 'got-s6e01-002', 'hotd-s1e10-006', 'got-s4e03-003', 'hotd-s1e01-006', 'got-s5e04-011'],
  pronunciation: { focus: 'stress', words: ['pendagon', 'otāpagon', 'vestragon', 'nēdegon', 'dīnagon'], lines: ['got-s3e04-013', 'got-s3e10-003', 'got-s3e09-002'] },
  letter: {
    fi: 'Kirjoita kolme toivetta: jos, jotta ja kunpa (subjunktiivi).',
    en: 'Write three wishes: if, so that and if only (subjunctive).',
  },
  scene: 'got-s5e05-1',
}

# Jos, jotta, kunpa: subjunktiivi sivulauseissa

Duolingossa subjunktiivi oli ennen kaikkea **kiellon muoto**: <Hv glossed>Rȳbon daor</Hv> ’en kuule’.
Tunteita käsittelevässä osiossa (<LessonLink n={32}>oppitunti 32</LessonLink>) Tips & Notes paljasti vähän enemmän: subjunktiivin ”alkuperäinen tarkoitus” on
sivulauseissa, joiden sisältö on epävarma, kuviteltu tai ehdollinen – ja <Hv glossed>jaelagon</Hv> ’toivoa’
vaatii sivulauseen verbin subjunktiiviin. Tällä oppitunnilla katsotaan, mitä muuta subjunktiivi tekee. Juuri näitä
rakenteita tarvitsette, kun kirjoitatte toisillenne ”jos olisit täällä”, ”jotta muistaisit” ja
”kunpa”.

Lähteenä on wikin sivu *Verb Moods*, joka on koottu Petersonin julkaisemista lauseista ja hänen
blogi- ja sähköpostivastauksistaan. Sivu sanoo itsekin monessa kohdassa ”näyttää” ja ”oletettavasti” –
ja niin sanomme mekin.

## Ensin kertaus: mikä laukaisee kieltosubjunktiivin?

Wikin mukaan kieltosubjunktiivin laukaisee nimenomaan sana <Hv>daor</Hv>. Muiden kieltävien sanojen –
<Hv glossed>dōrior</Hv> ’ei mikään’, <Hv glossed>daorys</Hv> ’ei kukaan’ – kanssa verbi on tavallisessa indikatiivissa:

<Example id="ex-e61df45a8982" />

<Line id="got-s3e04-013" />

Jälkimmäisessä lauseessa <Hv glossed>daorys ziry ōdrikilza</Hv> ’kukaan ei vahingoita häntä’ verbi on
indikatiivin futuurissa, vaikka lause on merkitykseltään kielteinen.

## Ehtolauseet: *lo*

Konjunktio <Hv glossed>lo</Hv> tarkoittaa ’jos’, mutta myös ’kun’ ja ’niin kuin’. Pääasia on tämä:

- Jos ehto on **todellinen** tai avoin – puhuja pitää sitä mahdollisena tai totena – sivulauseen verbi
  on **indikatiivissa**.
- Jos ehto on **kuviteltu** tai epävarma, verbi on **subjunktiivissa**.

### Todellinen ehto: indikatiivi

Daenerys Yunkain porteilla:

<Line id="got-s3e10-003" />

<Interlinear hv="Lo ziry arlī jael|āt, jemēlo syt ziry mazemagon jemo bēvil|za." gloss="jos se-ACC takaisin haluta-PRS.2PL te.itse-GEN puolesta se-ACC ottaa-INF te-GEN täytyä-PRS.3SG" fi="Jos haluatte sen takaisin, teidän on otettava se itse." />

Vapaudenhalu ei ole kuviteltua, joten <Hv>jaelāt</Hv> on indikatiivi.

<Note>
Wikin *Verb Moods* -sivu lainaa Petersonia juuri tästä *Mhysa*-jakson repliikistä: hän kertoo
harkinneensa siihen verbin <Hv>emagon</Hv> subjunktiivia, mutta piti sitä ”liian hypoteettisena” ja
valitsi suoremman indikatiivin. Valinta on siis puhujan: indikatiivi = ”otan tämän todesta”.
</Note>

Samoin Daenerysin kuuluisa lause johtajuudesta:

<Line id="got-s3e09-002" />

### Kuviteltu ehto: subjunktiivi

Daemon Rhaenyralle kolmannella kaudella:

<Line id="hotd-s3e02-003" />

<Interlinear hv="Lo drējor iks|os, sepār skoros gaom|ilū?" gloss="jos tosi-NOM olla-PRS.SBJV.3SG niin.sitten mikä-ACC tehdä-FUT.SBJV.2SG" fi="Jos se olisi totta, mitä sitten tekisit?" />

Repliikissä on kaksi ehtoa peräkkäin. Ensimmäinen, <Hv glossed>lo drējion issa</Hv> ’jos se on todellista’, on
indikatiivissa – Daemon toistaa, mitä on kuullut. Toinen, <Hv fi="jos se olisi totta">lo drējor iksos</Hv>, on
subjunktiivissa: nyt hän kuvittelee. Päälauseen alussa on <Hv glossed>sepār</Hv> ’niin sitten’, joka
aloittaa ehdon seurauksen, ja verbi on **futuurin subjunktiivissa** (<Hv>gaomilū</Hv>) – siitä
alempana lisää.

Samwell lukee vanhaa ennustusta:

<Line id="got-s5e05-002" />

<Interlinear hv="Lo ōños ñāqot umb|os, sȳndror endie jemēb|ilza." gloss="jos valo-NOM itä-DAT pysyä-PRS.SBJV.3SG pimeys-NOM.COL länsi-ACC hallita-FUT.3SG" fi="Jos valo jää itään, pimeys hallitsee länttä." />

Tässä seuraus on tavallisessa futuurissa: kuviteltu ehto, varma seuraus.

Rhaenyralla molemmat puolet ovat subjunktiivissa – ehto on kuviteltu ja kysymyskin retorinen:

<Line id="hotd-s3e05-009" />

Lyhyt ja hyödyllinen kiinteä ilmaus on <Hv glossed>lo iksos</Hv> ’jos niin on’:

<Line id="hotd-s2e04-009" />

Lohikäärmeenvartijan varoituksessa on rakenne <Hv>lo</Hv> + subjunktiivi + futuuri:

<Line id="hotd-s1e06-010" />

### *Lo* = ’kun (aina kun)’

Kun <Hv glossed>lo</Hv> tarkoittaa ’aina kun’, verbi voi olla subjunktiivissa: tilanne on yleinen eikä
mikään tietty tapaus.

<Line id="got-s6e08-002" />

Verbi <Hv>epos</Hv> on <Hv glossed>epagon</Hv>-verbin preesensin subjunktiivi: ’kun (ikinä) kuningatar
pyytää’.

### Vertailu suomeen

<Compare fi="ol|isi" hv="iks|os" label="konditionaali ~ subjunktiivi" />

Suomi merkitsee kuviteltua ehtoa konditionaalilla **molemmissa** lauseissa: ”jos se *olisi* totta,
mitä *tekisit*”. Valyriassa työnjako on toinen: sivulauseessa on preesensin subjunktiivi ja
päälauseessa joko futuurin subjunktiivi (”tekisit”) tai pelkkä futuuri (”hallitsee”). Todellisessa
ehdossa kielet toimivat samoin: indikatiivi molemmissa (”jos haluatte – teidän on otettava”).

## ”Haluaisin” – futuurin subjunktiivi toiveena

Wikin mukaan subjunktiivi voi korvata ’haluta’-verbin, ja tämä koskee erityisesti futuurin
subjunktiivia. Suomeksi se kääntyy luontevimmin konditionaalilla:

<Example id="ex-26431af6f541" />

<Interlinear hv="Vīlībāzmosa iderennī em|ilun." gloss="sota-INS oikeudenkäynti-ACC omistaa-FUT.SBJV.1SG" fi="Haluaisin taisteluoikeudenkäynnin." />

Tämä on yksi <LessonLink n={20}>oppitunnilla 20</LessonLink> kokonaisena opetelluista repliikeistä. Nyt näet sen rakenteen sana sanalta: ’sodalla’ instrumentaalissa, ’oikeudenkäynti’ akkusatiivissa ja verbi futuurin subjunktiivissa.

<Example id="ex-7f3b585ee8d2" />

Jälkimmäinen on wikin mukaan kohtelias ”haluaisitko mennä kanssani naimisiin?” – verbi on
<Hv glossed>dīnagon</Hv>-verbin futuurin passiivin subjunktiivi (’tulla vihityksi kanssani’).

Sama muoto esiintyy kysymyksissä, joissa ”haluaisit” saa moittivan sävyn:

<Line id="hotd-s2e07-003" />

<Note>
**Kirjeisiin:** futuurin subjunktiivi on kohtelias tapa sanoa ”haluaisin”, ”tekisin”. Muistakaa
*u*-päätteet: <Hv>emilun</Hv>, <Hv>emilū</Hv>, <Hv>emilus</Hv>.
</Note>

<Paradigm lemma="emagon" filter="subjunctive.active.future" />

## ”Kunpa” ja ”olisin” – toive ja kuviteltu menneisyys

Daemon Viserysille vaimostaan:

<Line id="hotd-s1e04-006" />

<Interlinear hv="Hegnīr iks|os jael|an." gloss="niin olla-PRS.SBJV.3SG toivoa-PRS.1SG" fi="Kunpa niin olisi." />

Kyse on jo tutusta <Hv glossed>jaelagon</Hv> + subjunktiivi -rakenteesta: ’toivon, että niin
olisi’. Toinen virke on kiinnostavampi: <Hv glossed>dāerēdoks</Hv> on **perfektin passiivin subjunktiivi**,
ja sillä Daemon kuvittelee menneisyyttä, jota ei tapahtunut: ’olisin päässyt eroon’. Suomessa tähän
tarvitaan konditionaalin perfekti (*olisin päässyt*); valyriassa riittää perfektin subjunktiivi.

## Tarkoitus: *kessyt* ja *hegnīr*

Sanakirjan mukaan <Hv glossed>kessyt</Hv> tarkoittaa ’jotta, siten että’, ja sen jälkeen verbi on subjunktiivissa:

<Example id="ex-62354dad9567" />

<Interlinear hv="Udriri Valyrio Eglio gūrēñagon aō bēvil|za kessyt Zaldrīzo Lenton majēb|ilū." gloss="kieli-ACC.COL Valyria-GEN korkea-GEN oppia-INF sinä-GEN täytyä-PRS.3SG jotta lohikäärme-GEN talo-ACC astua.sisään-FUT.SBJV.2SG" fi="Sinun on opittava korkeavalyrian kieli, jotta pääsisit Lohikäärmeen sukuun." />

Varys kääntää Tyrionin huonoa valyriaa, ja tarkoitus ilmaistaan sanalla <Hv l="hegnīr-3">hegnīr</Hv>:

<Line id="got-s6e01-002" />

<Interlinear hv="Gēlȳri aōt tepagon jael|as, hegnīr aōhys rūs ipradagon kost|os." gloss="raha-ACC sinä-DAT antaa-INF haluta-PRS.3SG niin.että sinun-SOL.NOM vauva-NOM syödä-INF voida-PRS.SBJV.3SG" fi="Hän haluaa vain antaa sinulle rahaa, jotta vauvasi voisi syödä." />

<Note>
**Sanalla on kaksi käyttöä.** Sanakirjan hakusana <Hv glossed>hegnīr</Hv> (etymologia *hae* ’kuin’ +
*konīr*, kirjaimellisesti ’kuin tuo’) on ensinnäkin adverbi ’niin, sillä tavalla’, ja siihen sopii
Daemonin *Hegnīr iksos jaelan*. Samassa hakusanassa on kuitenkin myös konjunktio ’niin, niin että’
(synonyymi <Hv>kessyt</Hv>), ja sen esimerkki on juuri tämä Varysin repliikki. *Verb Moods* -sivu
antaa samalle käytölle merkityksen ’jotta, tarkoituksella’. Kun tarkoituksen haluaa sanoa yksiselitteisesti,
<Hv>kessyt</Hv> on silti varmempi, koska <Hv glossed>hegnīr</Hv> voi tarkoittaa myös pelkkää ’niin, sillä
tavalla’.
</Note>

Suomi toimii tässä hämmästyttävän samoin: *jotta*-lauseessa käytetään usein konditionaalia (”jotta
vauvasi *voisi* syödä”), aivan kuten valyria käyttää subjunktiivia.

## Mahdollisuus: *kostilus* ja potentiaali

Jo tuttu <Hv glossed>kostilus</Hv> ’ole hyvä; ehkä’ on wikin mukaan alun perin <Hv glossed>kostagon</Hv>-verbin
futuurin subjunktiivi: ’jos se on mahdollista’. Kun se tarkoittaa ’ehkä’, verbi on usein
subjunktiivissa:

<Line id="hotd-s1e10-006" />

<Interlinear hv="Kostilus hen jaehoti gīmēdenon iks|os." gloss="ehkä luota jumala-DAT.PL varoitus-NOM olla-PRS.SBJV.3SG" fi="Hän lienee varoitus jumalilta." />

Tässä suomi tarjoaa täydellisen vastineen: **potentiaali**. *Hän lienee varoitus* ilmaisee saman
varauksellisuuden kuin <Hv fi="ehkä … on">kostilus … iksos</Hv>. Potentiaali on suomessa harvinainen ja juhlava, ja
niin on tämäkin kohtaus.

Daenerys tosin käyttää sanan <Hv l="kostilus" fi="ehkä">kostilus</Hv> kanssa indikatiivia (<Hv>vestretis</Hv>):

<Line id="got-s4e03-003" />

Wiki ei selitä eroa. Todennäköisin tulkinta (meidän, ei wikin): Daenerys pitää valheita varsin
todennäköisinä.

Subjunktiivi voi ilmaista mahdollisuutta ilman mitään apusanaa. Daemon istuu Rautavaltaistuimella:

<Line id="hotd-s1e01-006" />

<Hv>Sinilus</Hv> on <Hv glossed>siñagon</Hv>-verbin (’tulla joksikin’) futuurin subjunktiivi: ’saattaisi
tulla’.

## Epäsuora esitys

Wikin mukaan epäsuorassa esityksessä subjunktiivi on **valinnainen**. Indikatiivi viittaa siihen, että
puhuja on samaa mieltä; subjunktiivi siihen, ettei hän ole tai ei halua sitoutua väitteeseen.

<Example id="ex-df10690e339e" />

<Interlinear hv="Morghot nēdyssy sesīr zūg|usy azantys vestr|as." gloss="kuolema-DAT rohkea-NOM.PL jopa pelätä-AOR.SBJV.3PL ritari-NOM sanoa-PRS.3SG" fi="Ritari sanoo, että rohkeatkin pelkäävät kuolemaa." />

Vertailkaa Talisan kirjeeseen, jossa kerrotun asian verbi (<Hv l="sagon">issa</Hv>) on indikatiivissa – Talisa uskoo, mitä sanotaan:

<Example id="ex-13537ed19fcd" />

Hizdahr muistuttaa Daenerysia tämän valasta futuurin subjunktiivilla:

<Line id="got-s5e04-011" />

<Note>
Hizdahrin *kesosy* on Petersonin tahaton virhe (wikin alaviite; Petersonin Conlang Dialogue -selitykset): kollektiivi *valar* saa verbin yksikköön, joten odotettu muoto on <Hv l="sagon">kesos</Hv>. Peterson jätti muodon ennalleen, koska se sopii puhujalle.
</Note>

Kuten <Hv l="jaelagon">jaelagon</Hv>-rakenteessa, ”että”-sanaa ei ole: sivulause tulee ensin ja päälause perään.

### ”Näyttää siltä, että”

Sanakirjan mukaan <Hv glossed>vestragon</Hv> tarkoittaa persoonattomana ja subjunktiivin kanssa ’näyttää,
vaikuttaa joltakin’:

<Example id="ex-8bf9afdcc233" />

<Interlinear hv="Zāliapossa buq|ō ynot vestr|as." gloss="sipuli-ACC.PL vihata-PRS.SBJV.2SG minä-DAT sanoa-PRS.3SG" fi="Minusta näyttää siltä, että vihaat sipuleita." />

<Note>
**Suomi ei ilmaise etäisyyttä tapaluokalla.** ”Ritari sanoo, että rohkeatkin pelkäävät” on suomeksi
indikatiivissa, uskoi puhuja tai ei. Lähimmät keinot ovat sanat kuten *kuulemma* ja *muka*.
Valyriassa sama sävy syntyy verbin muodosta.
</Note>

## Yhteenveto

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Rakenne</th><th>Verbi</th><th>Esimerkki</th></tr></thead>
  <tbody>
    <tr><td>kielto <Hv>daor</Hv></td><td>subjunktiivi</td><td><Hv fi="ei ole">iksos daor</Hv></td></tr>
    <tr><td>todellinen ehto <Hv>lo</Hv></td><td>indikatiivi</td><td><Hv fi="jos … haluatte">lo … jaelāt</Hv></td></tr>
    <tr><td>kuviteltu ehto <Hv>lo</Hv></td><td>subjunktiivi</td><td><Hv fi="jos se olisi totta">lo drējor iksos</Hv></td></tr>
    <tr><td>”tekisit, haluaisin”</td><td>futuurin subjunktiivi</td><td><Hv glossed>gaomilū</Hv>, <Hv glossed>emilun</Hv></td></tr>
    <tr><td>”olisin päässyt eroon”</td><td>perfektin passiivin subjunktiivi</td><td><Hv>dāerēdoks</Hv></td></tr>
    <tr><td>tarkoitus <Hv>kessyt</Hv></td><td>subjunktiivi</td><td><Hv fi="jotta … astuisit sisään">kessyt … majēbilū</Hv></td></tr>
    <tr><td><Hv glossed>kostilus</Hv> ’ehkä’</td><td>usein subjunktiivi, myös indikatiivi</td><td><Hv fi="ehkä … on">kostilus … iksos</Hv></td></tr>
    <tr><td>epäsuora esitys</td><td>valinnainen subjunktiivi</td><td><Hv fi="… pelkäävät, hän sanoo">zūgusy … vestras</Hv></td></tr>
  </tbody>
</table>
</div>

## Harjoituslauseita

<Practice hv="Lo ziry jaelā, ziry tepinna." fi="Jos haluat sen, annan sen (sinulle)." />

<Practice hv="Lo zaldrīzes ikson, sepār sōvīlun." fi="Jos olisin lohikäärme, niin lentäisin." />

<Practice hv="Valyrio udriri gūrēñan, kessyt aōt bardvon." fi="Opiskelen valyrian kieltä, jotta voisin kirjoittaa sinulle." />

<Practice hv="Kostilus ziry gīmios." fi="Hän tiennee sen. (Ehkä hän tietää sen.)" />

## Sanasto

<Vocab />

## Tarkistus

<Check />
